/**
 * Products this contact is interested in, and — for a person — which
 * organizations they belong to.
 *
 * The interests checklist is where Step 2's admin-created PRODUCT lookups
 * finally show up in daily work: nothing here names a product, so a deployment
 * that sells something else gets its own list by editing seed data
 * (CLAUDE.md §1).
 */
import { useMutation, useQuery } from '@apollo/client';
import { Button, Card, Spinner } from '@meshhub/design-tokens/ui';
import { Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import {
  ADMIN_LOOKUPS_QUERY,
  CONTACTS_QUERY,
  CREATE_LOOKUP,
  REMOVE_AFFILIATION,
  SET_AFFILIATION,
  SET_INTERESTS,
} from '../../lib/queries.js';
import { useSession } from '../../lib/session.jsx';
import { VocabularyField } from '../VocabularyField.web.jsx';
import { S } from '../../lib/strings.js';
import {
  CheckboxField,
  ConfirmDialog,
  FormDialog,
  MutationError,
  SelectField,
  TextField,
} from '../FormKit.web.jsx';
import { Link } from 'react-router-dom';

export function InterestsPanel({ contact, onSaved, readOnly }) {
  const [error, setError] = useState(null);
  const { isAdmin } = useSession();
  const variables = { kind: 'PRODUCT', includeInactive: false };
  const { data, loading } = useQuery(ADMIN_LOOKUPS_QUERY, { variables });
  const [setInterests, { loading: saving }] = useMutation(SET_INTERESTS);
  // «اگر محصول موردنظر در لیست نبود، نامش را وارد کنید» — through the SAME
  // vocabulary mechanism every other picker uses, so this is not a second
  // product catalogue. The control is absent for a USER, who would be refused
  // at the BFF and again at the service.
  const [createLookup, createState] = useMutation(CREATE_LOOKUP, {
    refetchQueries: [{ query: ADMIN_LOOKUPS_QUERY, variables }],
    awaitRefetchQueries: true,
  });

  const products = data?.lookups ?? [];
  const chosen = new Set(contact.interests.map((i) => i.product.id));
  // A product deactivated after this contact showed interest still belongs on
  // the profile — it is history — so it is appended to the active list.
  const retired = contact.interests
    .map((i) => i.product)
    .filter((p) => !products.some((active) => active.id === p.id));

  async function toggle(productId) {
    setError(null);
    const next = chosen.has(productId)
      ? [...chosen].filter((id) => id !== productId)
      : [...chosen, productId];
    try {
      await setInterests({ variables: { contactId: contact.id, productIds: next } });
      await onSaved?.();
    } catch (err) {
      setError(err);
    }
  }

  return (
    <Card>
      <div className="mb-4 flex items-center gap-3">
        <h2 className="text-md font-bold">{S.contacts.detail.interests}</h2>
        {saving && <Spinner />}
      </div>

      <MutationError error={error} />

      {loading && products.length === 0 && <Spinner />}
      {!loading && products.length === 0 && contact.interests.length === 0 && (
        <p className="text-text-muted">{S.contacts.detail.noInterests}</p>
      )}

      <div className="flex flex-col gap-2">
        {products.map((product) => (
          <CheckboxField
            key={product.id}
            label={product.labelFa}
            checked={chosen.has(product.id)}
            disabled={readOnly || saving}
            onChange={() => toggle(product.id)}
          />
        ))}
        {retired.map((product) => (
          <CheckboxField
            key={product.id}
            label={`${product.labelFa} (${S.app.inactive})`}
            checked
            disabled
          />
        ))}
      </div>

      {!readOnly && (
        <div className="mt-4 border-t border-border pt-4">
          <VocabularyField
            label={S.quickAdd.product}
            value=""
            // Choosing an existing row here TICKS it, so the picker doubles as
            // "add one I can see" and "add one that is missing" — one control
            // for one intent, rather than a select beside a plus.
            onChange={(id) => id && !chosen.has(id) && toggle(id)}
            options={products}
            loading={loading}
            includeNone
            canCreate={isAdmin}
            codePrefix="PRODUCT"
            creating={createState.loading}
            createError={createState.error}
            onCreate={async ({ labelFa, code }) => {
              const { data: created } = await createLookup({
                variables: { input: { kind: 'PRODUCT', code, labelFa } },
              });
              const id = created?.createLookup?.id;
              if (id) await toggle(id);
              return id;
            }}
          />
        </div>
      )}
    </Card>
  );
}

export function AffiliationsPanel({ contact, onSaved, readOnly }) {
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [error, setError] = useState(null);
  const [removeAffiliation] = useMutation(REMOVE_AFFILIATION);

  const isPerson = contact.kind === 'PERSON';

  async function remove(affiliation) {
    setError(null);
    try {
      await removeAffiliation({
        variables: {
          personContactId: affiliation.personContactId,
          organizationContactId: affiliation.organizationContactId,
        },
      });
      await onSaved?.();
    } catch (err) {
      setError(err);
    }
  }

  return (
    <Card>
      <div className="mb-4 flex items-center gap-3">
        <h2 className="text-md font-bold">{S.contacts.detail.affiliations}</h2>
        {!readOnly && (
          <Button variant="ghost" className="ms-auto" onClick={() => setAdding(true)}>
            <Plus size={14} aria-hidden="true" />
            {S.contacts.detail.addAffiliation}
          </Button>
        )}
      </div>

      <MutationError error={error} />

      {contact.affiliations.length === 0 ? (
        <p className="text-text-muted">{S.contacts.detail.noAffiliations}</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {contact.affiliations.map((affiliation) => {
            // One row, read from whichever end this contact is not.
            const otherName = isPerson
              ? affiliation.organizationName
              : affiliation.personName;
            const otherId = isPerson
              ? affiliation.organizationContactId
              : affiliation.personContactId;
            return (
              <li key={affiliation.id} className="flex items-center gap-3">
                <Link to={`/contacts/${otherId}`} className="text-primary hover:underline">
                  {otherName}
                </Link>
                {affiliation.roleTitle && (
                  <span className="text-sm text-text-muted">{affiliation.roleTitle}</span>
                )}
                {!readOnly && (
                  <Button
                    variant="ghost"
                    className="ms-auto"
                    onClick={() => setRemoving(affiliation)}
                    aria-label={S.app.remove}
                    title={S.app.remove}
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {adding && (
        <AffiliationDialog
          contact={contact}
          open={adding}
          onOpenChange={setAdding}
          onSaved={onSaved}
        />
      )}

      <ConfirmDialog
        open={Boolean(removing)}
        onOpenChange={(next) => !next && setRemoving(null)}
        title={S.app.remove}
        message={
          removing
            ? S.contacts.detail.confirmRemoveAffiliation(
                isPerson ? removing.organizationName : removing.personName,
              )
            : ''
        }
        confirmLabel={S.app.remove}
        danger
        onConfirm={() => remove(removing)}
      />
    </Card>
  );
}

/**
 * Pick the counterpart and give them a role title.
 *
 * The picker searches the *other* kind: a person's dialog lists organizations
 * and vice versa. That mirrors the domain rule (person ↔ organization only) in
 * the UI, so the invalid combination is unreachable rather than merely refused.
 */
function AffiliationDialog({ contact, open, onOpenChange, onSaved }) {
  const isPerson = contact.kind === 'PERSON';
  const [search, setSearch] = useState('');
  const [chosenId, setChosenId] = useState('');
  const [roleTitle, setRoleTitle] = useState('');
  const [error, setError] = useState(null);

  const { data, loading } = useQuery(CONTACTS_QUERY, {
    variables: { page: 1, pageSize: 25, search: search.trim() || null },
  });
  const [setAffiliation, { loading: saving }] = useMutation(SET_AFFILIATION);

  const wanted = isPerson ? 'ORGANIZATION' : 'PERSON';
  const candidates = (data?.contacts?.items ?? []).filter(
    (c) => c.kind === wanted && c.id !== contact.id,
  );

  async function submit() {
    if (!chosenId) return;
    setError(null);
    try {
      await setAffiliation({
        variables: {
          personContactId: isPerson ? contact.id : chosenId,
          organizationContactId: isPerson ? chosenId : contact.id,
          roleTitle: roleTitle.trim() || null,
        },
      });
      await onSaved?.();
      onOpenChange(false);
    } catch (err) {
      setError(err);
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={S.contacts.detail.addAffiliation}
      onSubmit={submit}
      busy={saving}
    >
      <MutationError error={error} />

      <TextField
        label={isPerson ? S.contacts.detail.organization : S.contacts.detail.person}
        placeholder={
          isPerson ? S.contacts.detail.searchOrganization : S.contacts.detail.searchPerson
        }
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />

      <SelectField
        label={S.app.search}
        value={chosenId}
        onChange={(event) => setChosenId(event.target.value)}
        required
      >
        <option value="">{loading ? S.app.loading : S.app.none}</option>
        {candidates.map((candidate) => (
          <option key={candidate.id} value={candidate.id}>
            {candidate.displayName}
          </option>
        ))}
      </SelectField>

      <TextField
        label={S.contacts.detail.roleTitle}
        value={roleTitle}
        onChange={(event) => setRoleTitle(event.target.value)}
      />
    </FormDialog>
  );
}
